/**
 * Web Storage that cannot throw.
 *
 * ## Why this exists
 *
 * `localStorage` is not always readable. A browser set to block site data — or
 * a strict private window, or an embedded webview — throws on ACCESS:
 *
 *     SecurityError: Failed to read the 'localStorage' property from 'Window':
 *     Access is denied for this document.
 *
 * That is `WEFAQNA-FRONTEND-1`, the first error web Sentry ever received. It
 * arrived the day the CSP stopped blocking Sentry reports; before that it had
 * been happening with nobody able to see it.
 *
 * ## Why it was worse than a crash
 *
 * The throw was in `AuthContext`'s `initAuth()`:
 *
 *     const hasCachedSession = localStorage.getItem('user') || ...   // throws
 *     ...
 *     setLoading(false);        // never runs
 *     initAuth();               // no .catch()
 *
 * So `loading` stayed `true` forever. Those members did not see an error or a
 * broken page — they saw a spinner, permanently, with nothing to report and
 * nothing to retry. A visible crash would have been kinder.
 *
 * ## The contract
 *
 * Blocked storage behaves exactly like EMPTY storage: reads return `null`,
 * writes are dropped. That is the honest degradation — someone who blocked
 * site data has asked not to be remembered, and the app should work anyway
 * rather than refuse to start.
 *
 * Deliberately silent. These throws fire on every single call for an affected
 * user, so reporting them would bury Sentry for one member's browser setting
 * (see the 2026-06 quota work). The condition is a preference, not a defect.
 *
 * Note the store is read INSIDE the try. `window.localStorage` throws on
 * property access, not just on method call, so caching it at module scope
 * would move the crash to import time and take the whole bundle with it.
 */

function makeSafeStorage(read) {
  return {
    /** @returns {string|null} the value, or null if missing OR unreadable. */
    getItem(key) {
      try {
        return read().getItem(key);
      } catch {
        return null;
      }
    },
    /** @returns {boolean} whether the write actually persisted. */
    setItem(key, value) {
      try {
        read().setItem(key, value);
        return true;
      } catch {
        // Also covers QuotaExceededError, which Safari raises in private mode
        // on the FIRST write rather than on access.
        return false;
      }
    },
    /** @returns {boolean} whether the removal actually happened. */
    removeItem(key) {
      try {
        read().removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
}

export const safeLocalStorage = makeSafeStorage(() => window.localStorage);
export const safeSessionStorage = makeSafeStorage(() => window.sessionStorage);

/** True when storage is usable. For deciding whether to OFFER a "remember me"
 *  style feature — never required for the app to function. */
export function isStorageAvailable() {
  try {
    const probe = '__wefaqna_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export default safeLocalStorage;
